import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  Checkbox,
} from '@chakra-ui/react';
import { organizationRepository } from 'api/organizationRepository';
import axios from 'axios';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';
import { useAuth } from 'contexts/authContext';
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { flattenZodErrors, organizationSchema } from 'validations/Validations';
import { z } from 'zod';

export default function AddOrganizationForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const { role } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgData, setOrgData] = useState({

    name: '',
    email: '',
    contact: '',
    gsTin: '',
    isActive: true,
    hasAMC: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<'success' | 'info' | 'warning' | 'error'>('success');
  const centerAlertTimerRef = useRef<number | null>(null);

  const showCenteredAlert = (
    msg: string,
    status: 'success' | 'info' | 'warning' | 'error' = 'success',
    duration = 3000
  ) => {
    if (centerAlertTimerRef.current) {
      window.clearTimeout(centerAlertTimerRef.current);
      centerAlertTimerRef.current = null;
    }
    setCenterAlertMessage(msg);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
      centerAlertTimerRef.current = null;
    }, duration);
  };

  const validateField = (field: string, value: any) => {
    try {
      organizationSchema
        .pick({ [field]: true })
        .parse({ [field]: value });

      // clear error
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    } catch (err: unknown) {
      if (err instanceof z.ZodError) {
        const flatErrors = flattenZodErrors(err);

        setErrors(prev => ({
          ...prev,
          [field]: flatErrors[field],
        }));
      }
    }
  };


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === 'isActive') {
      const boolValue = value === 'true';

      setOrgData(prev => ({
        ...prev,
        isActive: boolValue,
      }));

      validateField('status', boolValue ? 'ACTIVE' : 'INACTIVE');
      return;
    }

    setOrgData(prev => ({
      ...prev,
      [name]: value,
    }));

    validateField(name, value);
  };

  const handleCheckboxChange = (e: any) => {
    setOrgData(prev => ({
      ...prev,
      hasAMC: e.target.checked,
    }));
  };


  const handleLeasedCheckboxChange = (e: any) => {
    const { name, checked } = e.target;

    setOrgData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const validatedData = {
        ...orgData,
        status: orgData.isActive ? 'ACTIVE' : 'INACTIVE',
      };
      organizationSchema.parse(validatedData);
      setErrors({});

      const created = await organizationRepository.addOrganization(validatedData);
      showCenteredAlert('Organization added successfully!', 'success');
      setTimeout(() => navigate('/admin/organizations/OrgList'), 2000);
    } catch (error: unknown) {
      if (error instanceof z.ZodError) {
        setErrors(flattenZodErrors(error));
        // showCenteredAlert('Please fill all required fields', 'warning');
      }
      else if (axios.isAxiosError(error)) {
        const backendMessage =
          error.response?.data?.message ||
          error.response?.data ||
          'Failed to add organization.';

        // GSTIN duplicate handling
        if (backendMessage.toString().toLowerCase().includes('gstin')) {
          setErrors(prev => ({
            ...prev,
            gsTin: backendMessage,
          }));
          showCenteredAlert(backendMessage, 'error');
        }
        else {
          showCenteredAlert(backendMessage, 'error');
        }
      }
      else {
        showCenteredAlert('Failed to add organization.', 'error');
      }
    }
    finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      overflow="hidden"
      align="center"
      justify="flex-start"
      position="relative"
      px={{ base: '20px', md: '40px' }}
      pb="60px"
    >
      <Box
        position="absolute"
        top="90"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        p="30px"
        mt={{ md: '150px' }}
        mx="auto"
        zIndex={1}
        overflowY="auto"
        borderRadius="16px"
      >
        <Flex justify="space-between" align="center" mb="20px">
          <Text color={textColor} fontSize="2xl" fontWeight="700" >
            Add Organization
          </Text>
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/organizations/OrgList')}
          />
        </Flex>

        <Flex direction="column" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <Stack direction="column" gap="20px">
              {/* Org Name */}
              <FormControl isInvalid={!!errors.name}>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Organization Name
                </Text>
                <InputField
                  name="name"
                  mb="0"
                  placeholder="Enter organization name"
                  value={orgData.name}
                  onChange={handleInputChange}
                />
                {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
              </FormControl>

              {/* Email */}
              <FormControl isInvalid={!!errors.email}>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Email
                </Text>
                <InputField
                  name="email"
                  type="email"
                  mb="0"
                  placeholder="Enter email"
                  value={orgData.email}
                  onChange={handleInputChange}
                />
                {errors.email && <FormErrorMessage>{errors.email}</FormErrorMessage>}
              </FormControl>

              {/* Contact */}
              <FormControl isInvalid={!!errors.contact}>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Contact Number
                </Text>
                <InputField
                  name="contact"
                  mb="0"
                  placeholder="Enter contact number"
                  value={orgData.contact}
                  onChange={handleInputChange}
                />
                {errors.contact && <FormErrorMessage>{errors.contact}</FormErrorMessage>}
              </FormControl>
            </Stack>

            <Stack direction="column" gap="20px">
              {/* Status */}
              <FormControl>
                <Text fontSize="sm" fontWeight="bold" mb="15px" color={textColor}>
                  Status
                </Text>
                <Select
                  name="isActive"
                  isDisabled
                  h="44px"
                  mb="0"
                  fontSize="sm"
                  value="ACTIVE"
                >
                  <option value="ACTIVE">Active</option>
                </Select>
              </FormControl>

              {/* GSTIN */}
              <FormControl isInvalid={!!errors.gsTin}>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  GSTIN
                </Text>
                <InputField
                  name="gsTin"
                  mb="0"
                  placeholder="Enter GSTIN"
                  value={orgData.gsTin}
                  onChange={handleInputChange}
                />
                {errors.gsTin && <FormErrorMessage>{errors.gsTin}</FormErrorMessage>}
              </FormControl>
            </Stack>

            {role === "Admin" && (
              <>
                <Checkbox
                  name="hasAMC"
                  isChecked={orgData.hasAMC}
                  onChange={handleCheckboxChange}
                >
                  <Text color={textColor}>
                    Is AMC required for this Organization?
                  </Text>
                </Checkbox>
              </>
            )}

          </SimpleGrid>

          <Flex justify="center" gap="20px" mt="24px">
            <Button
              variant="darkBrand"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: '128px', md: '148px' }}
              h="46px"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Submit
            </Button>
          </Flex>
        </Flex>
      </Card>


      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}
